import React, { useEffect, useRef, useState } from 'react';
import { studioFeatures, studioImages } from '../data/mock';
import { Camera, Headphones, Lightbulb, Palette } from 'lucide-react';

const featureIcons = [Camera, Headphones, Lightbulb, Palette];

const StudioSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveImage((prev) => (prev + 1) % studioImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} id="studio" className="py-24 bg-[#F5FDFF] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div>
            <h2
              className={`text-3xl md:text-4xl font-bold text-[#04101C] mb-6 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              World-Class <span className="bg-gradient-to-r from-[#00C4CC] to-[#00E08F] bg-clip-text text-transparent">Studio</span> & Production
            </h2>
            <p
              className={`text-[#04101C]/60 text-lg mb-10 transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              State-of-the-art facilities equipped with the latest technology to make your podcast look and sound incredible.
            </p>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {studioFeatures.map((feature, index) => {
                const Icon = featureIcons[index];
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-4 p-4 rounded-xl bg-white border border-[#04101C]/5 hover:border-[#00E08F]/30 hover:shadow-lg transition-all duration-300 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{ transitionDelay: `${(index + 2) * 100}ms` }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00C4CC]/10 to-[#00E08F]/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={22} className="text-[#00C4CC]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#04101C] mb-1">{feature.title}</h3>
                      <p className="text-[#04101C]/60 text-sm">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Image Gallery */}
          <div
            className={`relative transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            {/* Main Image */}
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
              {studioImages.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`Studio ${index + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                    index === activeImage ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#04101C]/60 via-transparent to-transparent" />

              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#00C4CC]/30 via-[#00E08F]/30 to-[#FFF7AE]/30 rounded-3xl blur-xl -z-10" />
            </div>

            {/* Image Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {studioImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImage(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === activeImage
                      ? 'w-6 bg-gradient-to-r from-[#00C4CC] to-[#00E08F]'
                      : 'bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-4 -left-4 px-6 py-3 rounded-2xl bg-white shadow-xl border border-[#00C4CC]/20">
              <p className="text-sm font-medium text-[#04101C]">Premium Equipment</p>
              <p className="text-xs text-[#00E08F]">Industry-leading quality</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StudioSection;
